/**
 * Integrytas Group - DSE Reporting Exposure & Index Advisor
 * Page: DSE.REPORTING
 *
 * Shows the flattened report columns a Dynamic Data View / report can use for an
 * entity (the dse_<entity>_flat virtual columns + types + indexed flag), and a
 * usage-driven Index Advisor: hot custom columns that are filtered/sorted often
 * but not yet indexed, with one-click functional-index creation.
 */

import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import type { AuthSession } from '../../types/auth';

interface ViewCol { key: string; label: string; data_type: string; sql_type: string; is_indexed: boolean }
interface ViewMeta { entity: string; view: string; columns: ViewCol[] }
interface Rec { entity: string; key: string; label: string; data_type: string; filter_count: number; sort_count: number; total: number; last_used_at: string | null }

export interface DseReportingPageProps { session: AuthSession }

export function DseReportingPage(props: DseReportingPageProps) {
  return (
    <PersonalizationProvider pageCode="DSE.REPORTING" session={props.session}>
      <DseReportingPageInner {...props} />
    </PersonalizationProvider>
  );
}

function DseReportingPageInner({ session }: DseReportingPageProps) {
  const { t } = useTranslation();
  const COLUMN_GRID_COLS: GridColumnDef[] = [
    { key: 'key',        header: t('dse_reporting.col.virtual_column', 'Virtual column'), defaultWidth: 200 },
    { key: 'label',      header: t('dse_reporting.col.label', 'Label'),          defaultWidth: 180 },
    { key: 'data_type',  header: t('dse_reporting.col.type', 'Type'),            defaultWidth: 120 },
    { key: 'sql_type',   header: t('dse_reporting.col.sql_cast', 'SQL cast'),    defaultWidth: 260 },
    { key: 'is_indexed', header: t('dse_reporting.col.indexed', 'Indexed'),      defaultWidth: 100 },
  ];
  const [entities, setEntities] = useState<string[]>([]);
  const [entity, setEntity] = useState('');
  const [meta, setMeta] = useState<ViewMeta | null>(null);
  const [recs, setRecs] = useState<Rec[]>([]);
  const [minUsage, setMinUsage] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // §17.B13: related-children expand/contract. A flattened virtual column is view
  // metadata with no child collections, so the disclosure renders inert.
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (_key: string): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (key: string) => buildRelatedGroups(key).some(g => g.items.length > 0);

  useEffect(() => { void (async () => {
    const { data } = await apiDb.from('dse_entity').select('entity_name').eq('is_enabled', 'Y').order('entity_name');
    setEntities(((data ?? []) as { entity_name: string }[]).map(r => r.entity_name));
  })(); }, []);

  const loadColumns = async (ent: string) => {
    if (!ent) { setMeta(null); return; }
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<ViewMeta>('dse_view_columns', { p_entity: ent });
      if (e) throw e;
      setMeta(data ?? null);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void loadColumns(entity);   }, [entity]);

  const loadRecs = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<Rec[]>('dse_index_recommendations', { p_min_usage: minUsage });
      if (e) throw e;
      setRecs(Array.isArray(data) ? data : []);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  // loadRecs is re-created each render; reload only when minUsage changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadRecs();   }, [minUsage]);

  const applyIndex = async (r: Rec) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string; index?: string }>('dse_apply_index', { p_entity: r.entity, p_attribute_key: r.key });
      if (e) throw e;
      if (data?.ok === false) setError(data.error_code ?? t('dse_reporting.index_failed', 'Index creation failed.'));
      else { setNote(t('dse_reporting.index_created', 'Created {{index}}.', { index: data?.index })); await loadRecs(); if (entity === r.entity) await loadColumns(entity); }
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('dse_reporting.title', 'Report Columns & Index Advisor')}</span>
        <button onClick={() => { void loadColumns(entity); void loadRecs(); }} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('dse_reporting.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('dse_reporting.working', 'Working…')}</span>}
        {note && <span className="text-csm-xs text-green-700">{note}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('dse_reporting.error_banner', "Couldn't complete the last report-columns operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => { void loadColumns(entity); void loadRecs(); }}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex-1 overflow-auto p-3">
        <section className="mb-5">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-csm-sm font-semibold text-csm-ink">{t('dse_reporting.flattened_columns', 'Flattened Report Columns')}</h3>
            <div className="w-56">
              <FilteredModalSingleSelect
                label={t('dse_reporting.select_entity', 'Select entity…')}
                options={entities.map(en => ({ internal_code: en, display_text: en }))}
                selectedCode={entity || null}
                onChange={(code) => setEntity(code ?? '')}
                placeholder={t('dse_reporting.select_entity', 'Select entity…')}
              />
            </div>
          </div>
          {meta && (
            <>
              <p className="mb-1 text-csm-2xs text-csm-watermark">{t('dse_reporting.view_label', 'View:')} <span className="font-mono">{meta.view}</span> {t('dse_reporting.view_hint_mid', '— point a report/grid at it (or')} <span className="font-mono">apiDb.from('{meta.entity}').flat()</span>{t('dse_reporting.view_hint_end', ') to filter/sort these as native typed columns.')}</p>
              <PersonalizedGrid componentKey="grid" columns={COLUMN_GRID_COLS}>
                {(visibleCols, widths) => (
                  <table className="w-full max-w-3xl border-collapse text-csm-sm">
                    <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                      <th className="w-7 px-2 py-1" title={t('dse_reporting.col.related', 'Related records')} />
                      {visibleCols.map(col => (
                        <th key={col.key} className="px-2 py-1"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {meta.columns.map(c => (
                        <Fragment key={c.key}>
                        <tr className="border-b border-csm-border-soft">
                          <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                            <RelatedExpandButton
                              open={detail.isOpen(c.key)}
                              hasChildren={hasRelated(c.key)}
                              onToggle={() => detail.toggle(c.key)}
                              expandLabel={t('dse_reporting.related.expand', 'Show related records')}
                              collapseLabel={t('dse_reporting.related.collapse', 'Hide related')}
                              tooltip={t('dse_reporting.related.tooltip', 'Show related records')}
                            />
                          </td>
                          {visibleCols.map(col => (
                            <td key={col.key} className="px-2 py-1"
                                style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                              {col.key === 'key'        && <span className="font-mono text-csm-xs">{c.key}</span>}
                              {col.key === 'label'      && <span className="text-csm-xs">{c.label}</span>}
                              {col.key === 'data_type'  && <span className="text-csm-2xs text-csm-ink-soft">{c.data_type}</span>}
                              {col.key === 'sql_type'   && <span className="font-mono text-csm-2xs text-csm-watermark">(ext_attributes-&gt;&gt;'{c.key}')::{c.sql_type}</span>}
                              {col.key === 'is_indexed' && <YesNoBadge value={c.is_indexed} yesLabel={t('dse_reporting.yes', 'yes')} noLabel={t('dse_reporting.no', 'no')} />}
                            </td>
                          ))}
                        </tr>
                        {detail.isOpen(c.key) && (
                          <tr>
                            <td colSpan={visibleCols.length + 1} className="bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                              <RelatedChildrenPanel groups={buildRelatedGroups(c.key)} session={session} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      ))}
                      {meta.columns.length === 0 && <tr><td colSpan={visibleCols.length + 1} className="px-2 py-6 text-center">
                        <EmptyState variant="no-data" title={t('dse_reporting.no_custom_columns', 'No custom columns for this entity.')} />
                      </td></tr>}
                    </tbody>
                  </table>
                )}
              </PersonalizedGrid>
            </>
          )}
        </section>

        <section>
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-csm-sm font-semibold text-csm-ink">{t('dse_reporting.index_advisor', 'Index Advisor')}</h3>
            <label className="text-csm-2xs text-csm-watermark">{t('dse_reporting.min_usage', 'min usage')}</label>
            <input type="number" min={1} value={minUsage} onChange={e => setMinUsage(Math.max(1, Number(e.target.value)))} className="h-7 w-16 rounded border border-csm-border px-1 text-csm-xs" />
            <span className="text-csm-2xs text-csm-watermark">{t('dse_reporting.advisor_hint', 'Hot custom columns (filtered/sorted often) that are not yet indexed.')}</span>
          </div>
          <table className="w-full max-w-3xl border-collapse text-csm-sm">
            <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
              <th className="px-2 py-1">{t('dse_reporting.col.entity', 'Entity')}</th><th className="px-2 py-1">{t('dse_reporting.col.column', 'Column')}</th><th className="px-2 py-1 csm-num">{t('dse_reporting.col.filters', 'Filters')}</th><th className="px-2 py-1 csm-num">{t('dse_reporting.col.sorts', 'Sorts')}</th><th className="px-2 py-1 csm-num">{t('dse_reporting.col.total', 'Total')}</th><th className="px-2 py-1">{t('dse_reporting.col.last_used', 'Last used')}</th><th className="px-2 py-1"></th>
            </tr></thead>
            <tbody>
              {recs.map(r => (
                <tr key={`${r.entity}.${r.key}`} className="border-b border-csm-border-soft">
                  <td className="px-2 py-1 font-mono text-csm-2xs text-csm-ink-soft">{r.entity}</td>
                  <td className="px-2 py-1 font-mono text-csm-xs">{r.key}</td>
                  <td className="px-2 py-1 text-csm-xs csm-num">{r.filter_count}</td>
                  <td className="px-2 py-1 text-csm-xs csm-num">{r.sort_count}</td>
                  <td className="px-2 py-1 text-csm-xs font-semibold csm-num">{r.total}</td>
                  <td className="px-2 py-1 text-csm-2xs text-csm-watermark">{r.last_used_at ? new Date(r.last_used_at).toLocaleString() : '—'}</td>
                  <td className="px-2 py-1"><button onClick={() => void applyIndex(r)} className="h-6 rounded border border-primary bg-primary px-2 text-csm-2xs text-white hover:opacity-90">{t('dse_reporting.create_index', 'Create index')}</button></td>
                </tr>
              ))}
              {recs.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center">
                <EmptyState variant="all-clear" title={t('dse_reporting.no_recommendations', 'No recommendations — no un-indexed custom columns have crossed the usage threshold yet.')} />
              </td></tr>}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
